import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { entries, keyBy, omit } from 'lodash';
import {
  Text,
  Flex,
  Thead,
  Th,
  Table,
  Tbody,
  Tr,
  Td,
  Button,
} from '@chakra-ui/react';
import { deleteData, fetchRowData, updateData } from '../../../api/tabsApi';
import { TState, useActions } from '../../../redux/store';
import { useCanI } from '../../../redux/actions';
import { getDiff } from '../../../helpers';
import Modal from '../../modal/Modal';

const ManageDuplicate = () => {
  const theme = useSelector((state: TState) => state.theme);
  const rowData = useSelector((state: TState) => state.rowData);
  const duplicateTab = useSelector((state: TState) => state.duplicateTab);
  const dataInputs = useSelector((state: TState) => state.dataInputs);
  const userList = useSelector((state: TState) => state.userList);
  const modalType = useSelector((state: TState) => state.modalType);

  const [originalRow, setOriginalRow] = useState<Record<string, any>>({});

  const { getData, onClose } = useActions();

  const canI = useCanI();

  const handleDeleteDuplicate = async () => {
    await deleteData(rowData._id);
    getData();
    onClose();
  };

  const handleAction = async (action: 'original' | 'duplicate' | 'both') => {
    switch (action) {
      case 'original':
        await deleteData(rowData._id);
        break;
      case 'duplicate':
        await deleteData(rowData._id);
        await updateData({
          ...(originalRow || {}),
          ...omit(rowData, originalRow ? ['_id', 'created_at'] : []),
          isDuplicate: false,
        });
        break;
      case 'both':
        await updateData({ ...rowData, isDuplicate: false });
        break;
    }

    getData();
    onClose();
  };

  const deletePermission = canI('roles', 'delete');

  const renderDeleteDuplicate = <Text>Confirmer la suppression ?</Text>;

  const diffs: Record<string, { old: any; new: any }> =
    modalType === 'editDuplicate' ? getDiff(originalRow, rowData) : {};

  const dataInputsByName = keyBy(dataInputs, 'name_id');
  const usersById = keyBy(userList, '_id');

  const rows = entries(diffs).map(([k, v]) => {
    const input =
      dataInputsByName[k.replace(/_street|_city|_dept|_postal/gi, '')];

    if (!input) return null;

    const isAddress = input.type === 'address';
    const isUser = input.type === 'users';
    const isMulti = input.type === 'multiselect';
    const isSwitch = input.type === 'switch';

    let prefix = '';

    if (isAddress) {
      prefix = /_street/.test(k)
        ? 'Rue'
        : /_city/.test(k)
        ? 'Ville'
        : /_dept/.test(k)
        ? 'Departement'
        : /_postal/.test(k)
        ? 'Code postal'
        : '';
    }

    if (isAddress && !prefix) return null;
    if (isUser && !usersById[v.old] && !usersById[v.new]) return null;

    return (
      <Tr>
        <Td>
          {prefix} {input?.label || input?.name_id?.replaceAll('_', ' ')}
        </Td>
        <Td>
          {isMulti
            ? v.old?.map((o: number) => input.options?.[o]).join(', ')
            : isSwitch && v.old === true
            ? 'OUI'
            : isSwitch && v.old === false
            ? 'NON'
            : isUser
            ? usersById[v.old]?.name || ''
            : input.options?.[v.old] || v.old}
        </Td>
        <Td>
          {isMulti
            ? v.new?.map((o: number) => input.options?.[o]).join(', ')
            : isSwitch && v.new === true
            ? 'OUI'
            : isSwitch && v.new === false
            ? 'NON'
            : isUser
            ? usersById[v.new]?.name || ''
            : input.options?.[v.new] || v.new}
        </Td>
      </Tr>
    );
  });

  const hasRow = rows.filter((r) => r).length;

  const renderManageDuplicate = (
    <>
      <Flex mb={3} gap={3} justifyContent='center'>
        <Button
          bgColor={theme.normal}
          color={theme.none}
          onClick={() => handleAction('original')}
        >
          Garder original et supprimer doublon
        </Button>
        <Button
          bgColor={theme.normal}
          color={theme.none}
          onClick={() => handleAction('duplicate')}
        >
          Garder doublon et écraser l'original
        </Button>
        <Button
          bgColor={theme.normal}
          color={theme.none}
          onClick={() => handleAction('both')}
        >
          Garder les deux
        </Button>
      </Flex>
      {!hasRow ? (
        "Les champs différents vous sont masqué ou il n'y a pas de différence"
      ) : (
        <Table>
          <Thead>
            <Tr>
              <Th>Champ</Th>
              <Th>Original</Th>
              <Th>Doublon</Th>
            </Tr>
          </Thead>
          <Tbody>{rows}</Tbody>
        </Table>
      )}
    </>
  );

  useEffect(() => {
    const gd = async () => {
      const rowId = rowData.isDuplicate;
      const tabId = duplicateTab._id;

      if (tabId && rowId) {
        const resp = await fetchRowData(tabId, rowId);
        setOriginalRow(resp.data[0]);
      }
    };

    gd();
  }, [rowData.isDuplicate]);

  const modalOptions = {
    editDuplicate: {
      component: renderManageDuplicate,
      canDelete: deletePermission ? 'deleteDuplicate' : '',
      title: 'Gérer doublon',
    },
    deleteDuplicate: {
      component: renderDeleteDuplicate,
      action: handleDeleteDuplicate,
      title: 'Supprimer doulon',
      actionLabel: 'Confirmer',
    },
  };

  return <Modal options={modalOptions} />;
};

export default ManageDuplicate;
